/* New conversation composer — the rich outbound-email modal used by the Support
   Inbox ("New conversation") and reused elsewhere (e.g. the sales-task form's
   "Message student → Send email"). It posts to the shared support
   create-conversation API via the support saga, so a student thread created here
   shows up in the Support Inbox too.

   Extracted verbatim from index.jsx so the /support behaviour is unchanged; the
   only additions are the optional `initial*` prefill props. */
/* eslint-disable react/prop-types, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import emojiData from '@emoji-mart/data';
import EmojiPicker from '@emoji-mart/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ChevronsUpDown, Paperclip, Search, Smile, X } from 'lucide-react';
import { getCookie } from '../../../util_functions';
import * as actions from '../../../redux/action_creators';
import {
  canonicalBodyHtml,
  cleanHtml,
  htmlToPlainText,
  searchUsers,
  hasDataImage,
  supportQuillFormats,
  supportQuillModules,
  uploadAttachment,
  useDropdown,
  useQuillPaste
} from './api_helpers';
import './support_inbox.css';

// Custom dropdown (button + popup) so it themes cleanly — a native <select>
// picks up the browser's own hover/focus ring which doesn't match the app.
function NcSelect({ label, placeholder, options, searchable, defaultValue = '', value, onChange }) {
  const { open, setOpen, ref } = useDropdown();
  const triggerRef = useRef(null);
  const [selected, setSelected] = useState(defaultValue);
  const touchedRef = useRef(false); // true once the user picks an option
  const [query, setQuery] = useState('');
  const [dropUp, setDropUp] = useState(false);
  // Controlled when a `value` prop is supplied; otherwise falls back to the
  // uncontrolled internal `selected` state (existing defaultValue usages).
  const isControlled = value !== undefined;
  const selectedKey = isControlled ? value : selected;
  const current = options.find((o) => o.key === selectedKey);

  // Adopt a late-arriving defaultValue (e.g. options/current-user load after
  // mount) as long as the user hasn't picked anything yet.
  useEffect(() => {
    if (!touchedRef.current && defaultValue) setSelected(defaultValue);
  }, [defaultValue]);
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  // On open, flip the menu upward when there isn't room below — the modal
  // clips overflow, so a downward menu near the bottom would be cut off.
  // Also reset the search box so it reopens clean.
  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const menuHeight = 300; // search + up to ~220px of options
    setDropUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
  }, [open]);

  return (
    <div className="si-select si-nc-select" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className="si-select-trigger"
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="si-select-value">
          {current ? current.label : <span className="si-select-ph">{placeholder}</span>}
        </span>
        <ChevronsUpDown size={15} className="si-select-caret" />
      </button>
      {open && (
        <div className={`si-select-menu ${dropUp ? 'si-select-menu-up' : ''}`}>
          {searchable && (
            <div className="si-select-search">
              <Search size={15} />
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                value={query}
                placeholder="Search"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}
          <div className="si-select-options">
            {filtered.length === 0 && <div className="si-select-empty">No matches</div>}
            {filtered.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`si-select-option ${selectedKey === o.key ? 'si-active' : ''}`}
                onClick={() => {
                  touchedRef.current = true;
                  if (!isControlled) setSelected(o.key);
                  if (onChange) onChange(o.key);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const NC_TYPES = [
  { key: 'student', label: 'Student' },
  { key: 'teacher', label: 'Teacher' }
];

// Email field wired to chat_server's user `/search` endpoint (mod_user_external).
// Typing queries by the current role (student/teacher); picking one fills in
// their email. Free-typing a brand-new email is still allowed. `initialValue`
// seeds the box so callers (e.g. the sales-task form) can prefill the student.
function NcContactSearch({ role, onChange, initialValue = '' }) {
  const { open, setOpen, ref } = useDropdown();
  const [value, setValue] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('none');
  const skipRef = useRef(false); // suppress the search that a pick would trigger

  // Surface the resolved email + name to the parent modal. Free-typing an email
  // reports { email, name:'' }; picking a person reports both.
  const report = (email, name) => {
    if (onChange) onChange(email, name);
  };

  const q = value.trim();
  // Teacher search matches from 1 char; student search requires ≥2 (per the API).
  const minLen = role === 'teacher' ? 1 : 2;

  useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      return undefined;
    }
    if (q.length < minLen) {
      setResults([]);
      setStatus('none');
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    setStatus('loading');
    setOpen(true);
    const id = setTimeout(() => {
      searchUsers(q, role)
        .then((users) => {
          if (!cancelled) {
            setResults(users);
            setStatus('success');
          }
        })
        .catch(() => {
          if (!cancelled) {
            setResults([]);
            setStatus('fail');
          }
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [q, role, minLen]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (u) => {
    skipRef.current = true;
    setValue(u.email);
    report(u.email, u.name || '');
    setOpen(false);
  };

  return (
    <div className="si-select si-nc-email" ref={ref}>
      <input
        id="si-nc-email"
        className="si-input"
        type="text"
        // Suppress Chrome's email/address autofill dropdown — this is a user
        // search box, not a place to fill the operator's own address. A
        // non-email name + these ignore hints keep browser/1Password/LastPass out.
        name="nc-contact-search"
        autoComplete="off"
        data-1p-ignore="true"
        data-lpignore="true"
        spellCheck={false}
        value={value}
        placeholder={`Search ${role}s by name, email or slug — or type a new email`}
        onChange={(e) => {
          setValue(e.target.value);
          // Free-typing clears any picked contact name — the typed value is the
          // email; the parent keeps whatever name a prior pick supplied unless
          // the user retypes, so report an empty name to reflect the manual edit.
          report(e.target.value, '');
        }}
        onFocus={() => q.length >= minLen && results.length > 0 && setOpen(true)}
      />
      {open && (
        <div className="si-select-menu">
          <div className="si-select-options">
            {status === 'loading' && <div className="si-select-empty">Searching…</div>}
            {status === 'fail' && <div className="si-select-empty">Search failed</div>}
            {status === 'success' && results.length === 0 && (
              <div className="si-select-empty">No {role}s found</div>
            )}
            {results.map((u) => (
              <button
                key={u.uuid || u.email}
                type="button"
                className="si-select-option"
                onClick={() => pick(u)}
              >
                <span className="si-nc-user">
                  <span className="si-nc-user-name">{u.name || '—'}</span>
                  <span className="si-nc-user-email">{u.email}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Basic email shape check — the same permissive pattern used across the CMS.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewConversationModal({
  onClose,
  onCreated,
  agents = [],
  teams = [],
  inboxes = [],
  refresh,
  dispatch,
  initialContactType = 'student',
  initialToEmail = '',
  initialContactName = ''
}) {
  const [contactType, setContactType] = useState(initialContactType);
  const [toEmail, setToEmail] = useState(initialToEmail);
  const [contactName, setContactName] = useState(initialContactName);
  const [subject, setSubject] = useState('');
  const [teamKey, setTeamKey] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]); // {name, id?, progress, failed}
  const fileRef = useRef(null);
  const quillRef = useRef(null);
  const emoji = useDropdown();
  const submittedRef = useRef(false);

  const createStatus = useSelector((s) => s.support.create_conversation_status);
  const createResult = useSelector((s) => s.support.create_conversation_result);
  const submitting = createStatus === 'loading';

  // Preselect the agent picker to the current CMS user. Agents are cms_user rows
  // (uuid + email), so match the logged-in user's email (`cms_email` cookie, with
  // redux cms_user_email as a fallback) against the agent email.
  const cmsUserEmail = useSelector((s) => s.home.cms_user_email);
  const selfKey = useMemo(() => {
    const email = (getCookie('cms_email') || cmsUserEmail || '').toLowerCase();
    if (!email) return '';
    const me = agents.find((a) => a.email && a.email.toLowerCase() === email);
    return me ? me.key : '';
  }, [agents, cmsUserEmail]);
  const [assigneeKey, setAssigneeKey] = useState('');
  // Adopt the resolved self agent once bootstrap agents load (mirrors NcSelect's
  // late-defaultValue behaviour), without clobbering a manual pick.
  const assigneeTouched = useRef(false);
  useEffect(() => {
    if (!assigneeTouched.current && selfKey) setAssigneeKey(selfKey);
  }, [selfKey]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Once the saga confirms the create succeeded, open the new thread and close.
  useEffect(() => {
    if (!submittedRef.current) return;
    if (createStatus === 'success') {
      submittedRef.current = false;
      const uuid = createResult && createResult.conversation && createResult.conversation.uuid;
      if (uuid && onCreated) onCreated(uuid);
      onClose();
    } else if (createStatus === 'fail') {
      submittedRef.current = false; // keep the draft so it can be retried
    }
  }, [createStatus, createResult, onCreated, onClose]);

  // Shared by the paperclip, and by images pasted/dropped into the editor.
  const uploadFiles = (picked) => {
    picked.forEach((file) => {
      const entry = { name: file.name, id: null, progress: 0, failed: false };
      setFiles((prev) => [...prev, entry]);
      uploadAttachment(file, (pct) =>
        setFiles((prev) => prev.map((f) => (f === entry ? { ...f, progress: pct } : f)))
      )
        .then((res) =>
          setFiles((prev) =>
            prev.map((f) =>
              f.name === entry.name && !f.id ? { ...f, id: res.id, progress: 100 } : f
            )
          )
        )
        .catch(() =>
          setFiles((prev) =>
            prev.map((f) => (f.name === entry.name && !f.id ? { ...f, failed: true } : f))
          )
        );
    });
  };

  const onFilePick = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  // Images pasted or dropped into the editor become attachments; 'image' is not
  // an allowed format, so Quill would otherwise simply discard them.
  useQuillPaste(quillRef, true, (file) => uploadFiles([file]));
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const bodyText = htmlToPlainText(message).trim();
  const anyUploading = files.some((f) => !f.id && !f.failed);
  const emailValid = EMAIL_RE.test(toEmail.trim());
  const canSubmit =
    emailValid && subject.trim().length > 0 && bodyText.length > 0 && !submitting && !anyUploading;

  const onSubmit = () => {
    if (!canSubmit) return;
    const attachment_ids = files.filter((f) => f.id).map((f) => f.id);
    const bodyHtml = canonicalBodyHtml(cleanHtml(message));
    // See the reply composer: a data: image never reaches the recipient.
    if (hasDataImage(bodyHtml)) {
      dispatch(
        actions.set_notification_variable(
          true,
          'error',
          "Inline images aren't delivered by email — remove the pasted image and use the attachment button instead."
        )
      );
      return;
    }
    const body = {
      to_email: toEmail.trim(),
      subject: subject.trim(),
      body_html: bodyHtml,
      body_text: bodyText
    };
    if (contactName.trim()) body.contact_name = contactName.trim();
    // teamKey comes from mapTeams options → the team uuid (team_uuid contract).
    if (teamKey) body.team_uuid = teamKey;
    // assigneeKey is a cms_user uuid (assignee_cms_uuid contract).
    if (assigneeKey) body.assignee_cms_uuid = assigneeKey;
    if (attachment_ids.length) body.attachment_ids = attachment_ids;
    submittedRef.current = true;
    dispatch(actions.create_support_conversation({ body, refresh }));
  };

  return (
    <div className="si-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="si-modal si-nc-modal"
        role="dialog"
        aria-modal="true"
        aria-label="New conversation"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="si-modal-close" aria-label="Close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="si-nc-head">
          <h2 className="si-modal-title">New conversation</h2>
          <div className="si-nc-toggle" role="radiogroup" aria-label="Contact type">
            {NC_TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                role="radio"
                aria-checked={contactType === t.key}
                className={`si-nc-toggle-opt ${contactType === t.key ? 'si-active' : ''}`}
                onClick={() => setContactType(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="si-form-label" htmlFor="si-nc-email">
          Email
        </label>
        <NcContactSearch
          role={contactType}
          initialValue={initialToEmail}
          onChange={(email, name) => {
            setToEmail(email);
            setContactName(name);
          }}
        />

        <div className="si-nc-grid">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="si-form-label" htmlFor="si-nc-subject">
              Subject
            </label>
            <input
              id="si-nc-subject"
              className="si-input !h-[40px]"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div>
            <span className="si-form-label">Inbox</span>
            {/* Inbox is a UI-only grouping (the folder nav) — the backend
                create-conversation contract has no inbox concept, so this select
                maps to nothing and is intentionally not sent. Kept uncontrolled
                for visual parity with the wireframe. */}
            <NcSelect label="Inbox" placeholder="Select inbox" options={inboxes} />
          </div>

          <div>
            <span className="si-form-label">Assign team (Optional)</span>
            <NcSelect
              label="Assign team"
              placeholder="Select team"
              options={teams}
              value={teamKey}
              onChange={setTeamKey}
            />
          </div>
          <div>
            <span className="si-form-label">Assign agent (Optional)</span>
            <NcSelect
              label="Assign agent"
              placeholder="Select agent"
              options={agents}
              value={assigneeKey}
              onChange={(k) => {
                assigneeTouched.current = true;
                setAssigneeKey(k);
              }}
              searchable
            />
          </div>
        </div>

        <span className="si-form-label">Message</span>
        {/* Rich-text body. Enter inserts a newline in Quill — it never sends;
            only the explicit Submit button does. */}
        <ReactQuill
          ref={quillRef}
          className="si-quill si-nc-quill"
          theme="snow"
          value={message}
          onChange={setMessage}
          modules={supportQuillModules}
          formats={supportQuillFormats}
          placeholder="Write a message…"
        />

        {files.length > 0 && (
          <div className="si-attachments">
            {files.map((f, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`${f.name}-${i}`} className="si-tag-chip si-att-chip">
                <Paperclip size={12} /> {f.name}
                {f.failed && <span className="si-att-progress si-att-failed">failed</span>}
                {!f.failed && !f.id && <span className="si-att-progress">{f.progress}%</span>}
                <button type="button" aria-label={`Remove ${f.name}`} onClick={() => removeFile(i)}>
                  <X size={13} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="si-nc-foot">
          <div className="si-nc-foot-tools">
            <button
              type="button"
              className="si-nc-tool"
              aria-label="Attach file"
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              <Paperclip size={18} />
            </button>
            <input ref={fileRef} type="file" multiple hidden onChange={onFilePick} />

            <div className="si-emoji" ref={emoji.ref}>
              <button
                type="button"
                className="si-nc-tool"
                aria-label="Insert emoji"
                onClick={() => emoji.setOpen((o) => !o)}
              >
                <Smile size={18} />
              </button>
              {emoji.open && (
                <div className="si-emoji-pop">
                  <EmojiPicker
                    data={emojiData}
                    onEmojiSelect={(e) => {
                      setMessage((m) => m + e.native);
                      emoji.setOpen(false);
                    }}
                    theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                    previewPosition="none"
                    skinTonePosition="none"
                  />
                </div>
              )}
            </div>
          </div>
          <button type="button" className="si-nc-submit" disabled={!canSubmit} onClick={onSubmit}>
            {submitting ? 'Sending…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
